import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MenuTriggerState } from 'react-stately';

import { TAmenitySlug } from '@/constants/amenitiesFilter';
import { TCancelPoliciesFilters } from '@/constants/moreFilter';
import { EFilterCategories, ESearchFilters } from '@/constants/searchFilters';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useIsLocalityPage } from '@/hooks/useIsLocalityPage';
import { EHeaderSearchFilters, setHeaderSearchFilter } from '@/redux/modules/globalHeader';
import {
  applySearchFilter,
  GET_VEHICLE_INFO_RESPONSE,
  GET_VEHICLES_MAKE_RESPONSE,
  GET_VEHICLES_MODEL_RESPONSE,
  setSelectedFilter,
  unsetSearchFilter,
} from '@/redux/modules/search';
import { clearFormFilter, ISearchFormFilters, setFormFilter } from '@/redux/modules/searchForm';
import { TRootState } from '@/redux/rootReducer';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { getSearchResultsMeta } from '@/redux/selectors/search/meta';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';
import { TVehicleType } from '@/services/types/search/rentals/id';
import { getParamAsString } from '@/utility/queryParams';

import FilterDropdownContainer from '../FilterDropdownContainer/FilterDropdownContainer';
import FilterModalContainer from '../FilterModal/FilterModalContainer';
import { useFilterPillsCtx } from '../FilterPillsContext/FilterPillsContext';

interface IFiltersPillsContentContainerProps {
  triggerRef: React.RefObject<HTMLElement>;
  isModalOpen: boolean;
  isDropdownOpen: boolean;
  handleDropdownDismiss: () => void;
  handleModalDismiss: () => void;
  dropdownTriggerState: MenuTriggerState;
}

export enum EDeliveryFilterStep {
  PICKUP = 1,
  DELIVERY = 2,
}

const FiltersPillsContentContainer: React.FC<IFiltersPillsContentContainerProps> = ({
  triggerRef,
  isModalOpen,
  isDropdownOpen,
  handleDropdownDismiss,
  handleModalDismiss,
  dropdownTriggerState,
}) => {
  const { isMobile } = useBreakpoint();
  const dispatch = useDispatch();
  const { isAutocompleteOpen } = useFilterPillsCtx();
  const { isLocalityPage } = useIsLocalityPage();
  const {
    priceHistogram: { maxValue: maxPriceFromMeta },
  } = useSelector(getSearchResultsMeta);
  const searchFormFilters = useSelector<TRootState, Partial<ISearchFormFilters>>(
    state => state.searchForm.filters,
  );
  const shouldMergeForm = useSelector((state: TRootState) => state.localitySearch.shouldMergeForm);
  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );

  const queryParams = useSelector(getQueryParams);
  const filtersFromQuery = useMemo(() => {
    // Start with query params and initial params
    const baseFilters = { ...queryParams };

    if (!Object.keys(searchFormFilters).length || !shouldMergeForm) {
      return baseFilters;
    }
    // Add any form state values that aren't already present on first render -
    // Now that locality pages have search results, some form fields come in NOT in query params
    Object.entries(searchFormFilters).forEach(([key, value]) => {
      if (value !== undefined && !(key in baseFilters)) {
        baseFilters[key] = `${value}`;
      }
    });
    return baseFilters;
  }, [queryParams, searchFormFilters, shouldMergeForm]);

  const {
    [ESearchFilters.PRICE_MIN]: pMin,
    [ESearchFilters.PRICE_MAX]: pMax,
    [ESearchFilters.DELIVERY]: deliveryFromQuery,
    [ESearchFilters.DELIVERY_STATIONARY]: deliveryStationaryFromQuery,
    [ESearchFilters.DELIVERY_ADDRESS]: deliveryAddressFromQuery,
    [ESearchFilters.FILTER_FEATURE]: featuresFromQuery,
    [ESearchFilters.SEARCH_DELIVERY_CAMPGROUND_ID]: deliveryCampgroundIdFromQuery,
    [ESearchFilters.BOUNDS_NE]: boundsNEFromQuery,
    [ESearchFilters.BOUNDS_SW]: boundsSWFromQuery,
    [ESearchFilters.ADDRESS]: addressFromQuery,
    [ESearchFilters.MAPBOX_ID]: mapboxIdFromQuery,
    [ESearchFilters.FULL_MAPBOX_ADDRESS]: fullMapboxAddressFromQuery,
  } = filtersFromQuery;

  const [deliveryFilterStep, setDeliveryFilterStep] = useState<EDeliveryFilterStep>(
    deliveryFromQuery === 'true' ? EDeliveryFilterStep.DELIVERY : EDeliveryFilterStep.PICKUP,
  );

  const resetPageParams: Partial<Record<ESearchFilters, any>> = useMemo(
    () => ({
      [ESearchFilters.PAGE_OFFSET]: String(0),
    }),
    [],
  );

  const handlePriceFilterSubmit = useCallback(() => {
    const price = {
      [ESearchFilters.PRICE_MIN]: searchFormFilters[ESearchFilters.PRICE_MIN],
      [ESearchFilters.PRICE_MAX]: searchFormFilters[ESearchFilters.PRICE_MAX],
    };
    // TODO: move this type out of the function.
    type TPriceKey = keyof typeof price;

    const unsetPrice = Object.keys(price).filter(k => {
      const value = price[k as TPriceKey];
      return (
        typeof value === 'undefined' ||
        (k === ESearchFilters.PRICE_MAX && value && value >= maxPriceFromMeta)
      );
    });
    const setPrice = Object.keys(price).reduce<Partial<Record<TPriceKey, string>>>((acc, cur) => {
      const key = cur as TPriceKey;
      if (unsetPrice.includes(key)) return acc;
      return { ...acc, [key]: String(price[key]) };
    }, {});

    unsetPrice.length && dispatch(unsetSearchFilter(unsetPrice as ESearchFilters[]));
    !isEmpty(setPrice) &&
      dispatch(
        applySearchFilter(
          {
            ...resetPageParams,
            ...setPrice,
          },
          false,
          false,
          false,
          undefined,
          false,
        ),
      );
  }, [searchFormFilters, dispatch, resetPageParams, maxPriceFromMeta]);

  const handlePriceFilterDismiss = useCallback(() => {
    const price = {
      [ESearchFilters.PRICE_MIN]: (pMin && Number(pMin)) || undefined,
      [ESearchFilters.PRICE_MAX]: (pMax && Number(pMax)) || undefined,
    };

    type TPriceKey = keyof typeof price;

    const unsetPrice = Object.keys(price).filter(k => {
      const value = price[k as TPriceKey];
      return (
        typeof value === 'undefined' ||
        (k === ESearchFilters.PRICE_MAX && value && value >= maxPriceFromMeta)
      );
    });
    const setPrice = Object.keys(price).reduce<Partial<ISearchFormFilters>>((acc, cur) => {
      const key = cur as TPriceKey;
      if (unsetPrice.includes(key)) return acc;
      return { ...acc, [key]: String(price[key]) };
    }, {});

    unsetPrice.length && dispatch(clearFormFilter({ filters: unsetPrice as ESearchFilters[] }));
    dispatch(setSelectedFilter(null));
    !isEmpty(setPrice) &&
      dispatch(
        setFormFilter({ filters: setPrice as ISearchFormFilters, filterMethod: null }, false),
      );
  }, [dispatch, maxPriceFromMeta, pMax, pMin]);

  const handleDeliveryFilterSubmit = useCallback(() => {
    const {
      [ESearchFilters.DELIVERY]: deliveryFromStore,
      [ESearchFilters.DELIVERY_ADDRESS]: deliveryAddressFromStore,
      [ESearchFilters.DELIVERY_STATIONARY]: deliveryStationaryFromStore,
      [ESearchFilters.DELIVERY_CENTER]: deliveryCenterFromStore,
      [ESearchFilters.DELIVERY_DETAILS]: deliveryDetailsFromStore,
      [ESearchFilters.DELIVERY_QUERY]: deliveryQueryFromStore,
      [ESearchFilters.DELIVERY_LOCATION_ID]: deliveryLocationIdFromStore,
      [ESearchFilters.BOUNDS_NE]: boundsNEFromStore,
      [ESearchFilters.BOUNDS_SW]: boundsSWFromStore,
      [ESearchFilters.GEOIP_ADDRESS]: geoipFromStore,
      [ESearchFilters.SEARCH_DELIVERY_CAMPGROUND_ID]: deliveryCampgroundIdFromStore,
      [ESearchFilters.ADDRESS]: addressFromStore,
      [ESearchFilters.MAPBOX_ID]: mapboxIdFromStore,
      [ESearchFilters.FULL_MAPBOX_ADDRESS]: fullMapboxAddressFromStore,
    } = searchFormFilters;

    const shouldApplySearchFilter =
      deliveryAddressFromStore ||
      deliveryStationaryFromStore ||
      deliveryCenterFromStore ||
      deliveryLocationIdFromStore ||
      deliveryQueryFromStore ||
      isLocalityPage;

    setDeliveryFilterStep(
      deliveryFromStore ? EDeliveryFilterStep.DELIVERY : EDeliveryFilterStep.PICKUP,
    );

    if (shouldApplySearchFilter) {
      dispatch(
        applySearchFilter(
          {
            ...resetPageParams,
            [ESearchFilters.DELIVERY]: String(deliveryFromStore),
            ...(deliveryAddressFromStore && {
              [ESearchFilters.ADDRESS]: String(deliveryAddressFromStore),
            }),
            ...(deliveryAddressFromStore && {
              [ESearchFilters.DELIVERY_ADDRESS]: String(deliveryAddressFromStore),
            }),
            ...(deliveryCenterFromStore && {
              [ESearchFilters.DELIVERY_CENTER]: deliveryCenterFromStore,
            }),
            ...(deliveryDetailsFromStore && {
              [ESearchFilters.DELIVERY_DETAILS]: deliveryDetailsFromStore,
            }),
            ...(deliveryLocationIdFromStore && {
              [ESearchFilters.DELIVERY_LOCATION_ID]: String(deliveryLocationIdFromStore),
            }),
            ...(deliveryQueryFromStore && {
              [ESearchFilters.DELIVERY_QUERY]: String(deliveryQueryFromStore),
            }),
            ...(mapboxIdFromStore && {
              [ESearchFilters.MAPBOX_ID]: mapboxIdFromStore,
            }),
            ...(fullMapboxAddressFromStore && {
              [ESearchFilters.FULL_MAPBOX_ADDRESS]: fullMapboxAddressFromStore,
            }),
            // The bounds remain saved in the search form and are not only used when returning to the 'pick it up' stage.
            [ESearchFilters.BOUNDS_NE]: undefined,
            [ESearchFilters.BOUNDS_SW]: undefined,
            [ESearchFilters.DELIVERY_STATIONARY]: String(deliveryStationaryFromStore),
            [ESearchFilters.SEARCH_DELIVERY_CAMPGROUND_ID]: deliveryCampgroundIdFromStore
              ? String(deliveryCampgroundIdFromStore)
              : undefined,
          },
          false,
          false,
          false,
          undefined,
          false,
        ),
      );
      dispatch(
        setFormFilter(
          {
            filters: {
              ...(boundsNEFromQuery && {
                [ESearchFilters.BOUNDS_NE]: String(boundsNEFromQuery),
              }),
              ...(boundsSWFromQuery && {
                [ESearchFilters.BOUNDS_SW]: String(boundsSWFromQuery),
              }),
              ...(addressFromQuery && {
                [ESearchFilters.ADDRESS]: String(addressFromQuery),
              }),
            },
            filterMethod: null,
          },
          true,
        ),
      );

      if (deliveryAddressFromStore) {
        dispatch(
          setHeaderSearchFilter({
            [ESearchFilters.ADDRESS]: String(deliveryAddressFromStore),
            // Also preserve mapbox values in header search filter
            ...(mapboxIdFromStore && {
              [ESearchFilters.MAPBOX_ID]: mapboxIdFromStore,
            }),
            ...(fullMapboxAddressFromStore && {
              [ESearchFilters.FULL_MAPBOX_ADDRESS]: fullMapboxAddressFromStore,
            }),
          }),
        );
      }
    } else {
      const existsFilters = {
        ...(boundsNEFromStore && { [ESearchFilters.BOUNDS_NE]: boundsNEFromStore }),
        ...(boundsSWFromStore && { [ESearchFilters.BOUNDS_SW]: boundsSWFromStore }),
        ...(geoipFromStore && { [ESearchFilters.GEOIP_ADDRESS]: geoipFromStore }),
        ...(addressFromStore && { [ESearchFilters.ADDRESS]: addressFromStore }),
      };
      dispatch(
        unsetSearchFilter(
          [
            ESearchFilters.DELIVERY,
            ESearchFilters.DELIVERY_ADDRESS,
            ESearchFilters.DELIVERY_CENTER,
            ESearchFilters.DELIVERY_LOCATION_ID,
            ESearchFilters.DELIVERY_QUERY,
            ESearchFilters.DELIVERY_STATIONARY,
            ESearchFilters.SEARCH_DELIVERY_CAMPGROUND_ID,
            ESearchFilters.DELIVERY_DETAILS,
          ],
          existsFilters,
        ),
      );
    }
  }, [
    isLocalityPage,
    searchFormFilters,
    dispatch,
    resetPageParams,
    boundsNEFromQuery,
    boundsSWFromQuery,
    addressFromQuery,
  ]);

  const handleDeliveryFilterDismiss = useCallback(() => {
    // Do not dismiss if autocomplete is open
    if (isAutocompleteOpen) {
      return;
    }

    const hadDeliveryBefore = deliveryFromQuery === 'true';

    setDeliveryFilterStep(
      hadDeliveryBefore ? EDeliveryFilterStep.DELIVERY : EDeliveryFilterStep.PICKUP,
    );

    if (hadDeliveryBefore) {
      const filters: Partial<ISearchFormFilters> = {
        [ESearchFilters.DELIVERY]: hadDeliveryBefore,
        [ESearchFilters.DELIVERY_STATIONARY]: deliveryStationaryFromQuery as string,
        [ESearchFilters.DELIVERY_ADDRESS]: deliveryAddressFromQuery as string,
        ...(mapboxIdFromQuery && {
          [ESearchFilters.MAPBOX_ID]: mapboxIdFromQuery as string,
        }),
        ...(fullMapboxAddressFromQuery && {
          [ESearchFilters.FULL_MAPBOX_ADDRESS]: fullMapboxAddressFromQuery as string,
        }),
      };
      if (deliveryCampgroundIdFromQuery) {
        filters[ESearchFilters.SEARCH_DELIVERY_CAMPGROUND_ID] = Number(
          deliveryCampgroundIdFromQuery,
        );
      }
      dispatch(
        setFormFilter({
          filters,
          filterMethod: null,
        }),
      );
    } else {
      dispatch(
        setFormFilter({
          filters: {
            [ESearchFilters.DELIVERY]: false,
          },
          filterMethod: null,
        }),
      );

      dispatch(
        clearFormFilter({
          filters: [
            ESearchFilters.DELIVERY_STATIONARY,
            ESearchFilters.DELIVERY_ADDRESS,
            ESearchFilters.SEARCH_DELIVERY_CAMPGROUND_ID,
          ],
        }),
      );
    }
  }, [
    deliveryAddressFromQuery,
    deliveryCampgroundIdFromQuery,
    deliveryFromQuery,
    deliveryStationaryFromQuery,
    mapboxIdFromQuery,
    fullMapboxAddressFromQuery,
    dispatch,
    isAutocompleteOpen,
    setDeliveryFilterStep,
  ]);

  const handleFiltersReset = useCallback(
    (
      filtersToReset: ESearchFilters[],
      filtersToRestore: ESearchFilters[] = [],
      valuesToRestore?: Partial<ISearchFormFilters>,
    ) => {
      const featuresFromQueryArray = getParamAsString(featuresFromQuery)?.split(
        ',',
      ) as TAmenitySlug[];

      // clearFormFilter will set this list as the new features list
      // check if pet_friendly and accessible are set in the query
      const featureListToKeep =
        featuresFromQueryArray?.filter(
          feature => feature === 'pet_friendly' || feature === 'accessible',
        ) || [];

      // If we're resetting FILTER_FEATURE, we should also clear pet_friendly
      if (filtersToReset.includes(ESearchFilters.FILTER_FEATURE)) {
        dispatch(
          clearFormFilter({
            filters: filtersToReset,
            filtersToRestore,
            filterFeatures: {
              [ESearchFilters.FILTER_FEATURE]: featureListToKeep.filter(
                feature => feature !== 'pet_friendly',
              ),
            },
          }),
        );
      } else {
        dispatch(
          clearFormFilter({
            filters: filtersToReset,
            filtersToRestore,
            filterFeatures: {
              [ESearchFilters.FILTER_FEATURE]: featureListToKeep,
            },
          }),
        );
      }

      // Reset type filters make, model, info
      if (filtersToReset.includes(ESearchFilters.FILTER_TYPE)) {
        [
          GET_VEHICLES_MAKE_RESPONSE,
          GET_VEHICLES_MODEL_RESPONSE,
          GET_VEHICLE_INFO_RESPONSE,
        ].forEach(type => {
          dispatch({
            type,
            payload: [],
          });
        });
      }

      // After clearing form filters, also trigger an actual search API call
      // by unsetting the filters from the query params (similar to other filter resets)

      // Build the filters to restore for the API call
      const filtersToRestoreForApi: Record<string, any> = {};

      // First, populate with current query values for all filters
      filtersToRestore.forEach(filter => {
        const value = filtersFromQuery[filter];
        if (value) {
          filtersToRestoreForApi[filter] = value;
        }
      });

      // Then, override any fields that exist in valuesToRestore with specific values for main and preview calls
      const searchFormFiltersToUpdate: Partial<ISearchFormFilters> = {};
      if (valuesToRestore) {
        Object.keys(valuesToRestore).forEach(field => {
          const originalValue = valuesToRestore[field as keyof typeof valuesToRestore];
          if (originalValue) {
            filtersToRestoreForApi[field] = originalValue;
          } else {
            filtersToReset.push(field as ESearchFilters);
          }

          searchFormFiltersToUpdate[field as keyof ISearchFormFilters] = originalValue as any;
        });
      }

      dispatch(unsetSearchFilter(filtersToReset, filtersToRestoreForApi));
      dispatch(
        setFormFilter({
          filters: searchFormFiltersToUpdate,
          filterMethod: null,
        }),
      );

      // Restore valid filter for UI
      if (valuesToRestore) {
        const validHeaderFields = Object.values(EHeaderSearchFilters);
        const searchFormFiltersToRestore: Record<string, any> = {};
        Object.keys(valuesToRestore).forEach(field => {
          const originalValue = valuesToRestore[field as keyof typeof valuesToRestore];
          if (validHeaderFields.includes(field as EHeaderSearchFilters)) {
            searchFormFiltersToRestore[field] = originalValue;
          }
        });
        dispatch(setHeaderSearchFilter(searchFormFiltersToRestore));
      }
    },
    [dispatch, featuresFromQuery, filtersFromQuery],
  );

  const handleMoreFilterSubmit = useCallback(() => {
    let more = {
      [ESearchFilters.FILTER_TYPE]: searchFormFilters[ESearchFilters.FILTER_TYPE],
      [ESearchFilters.CANCEL_POLICY]: searchFormFilters[ESearchFilters.CANCEL_POLICY],
      [ESearchFilters.FILTER_FEATURE]: searchFormFilters[ESearchFilters.FILTER_FEATURE],
      [ESearchFilters.FILTER_KEYWORDS]: searchFormFilters[ESearchFilters.FILTER_KEYWORDS],
      [ESearchFilters.LENGTH_BETWEEN]: searchFormFilters[ESearchFilters.LENGTH_BETWEEN],
      [ESearchFilters.SCORE]: searchFormFilters[ESearchFilters.SCORE],
      [ESearchFilters.UNLIMITED_MILES]: searchFormFilters[ESearchFilters.UNLIMITED_MILES],
      [ESearchFilters.UNLIMITED_GENERATOR]: searchFormFilters[ESearchFilters.UNLIMITED_GENERATOR],
      [ESearchFilters.WEIGHT_GREATER]: searchFormFilters[ESearchFilters.WEIGHT_GREATER],
      [ESearchFilters.WEIGHT_LESSER]: searchFormFilters[ESearchFilters.WEIGHT_LESSER],
      [ESearchFilters.SEATBELTS]: searchFormFilters[ESearchFilters.SEATBELTS],
      [ESearchFilters.FILTER_VEHICLE_AMPS]: searchFormFilters[ESearchFilters.FILTER_VEHICLE_AMPS],
      [ESearchFilters.INSTANT_BOOK]: searchFormFilters[ESearchFilters.INSTANT_BOOK],
      [ESearchFilters.FILTER_OWNERTYPE]: searchFormFilters[ESearchFilters.FILTER_OWNERTYPE],
      [ESearchFilters.FILTER_TAGS_SLUG]: searchFormFilters[ESearchFilters.FILTER_TAGS_SLUG],
      [ESearchFilters.FILTER_RENTER_AGE]: searchFormFilters[ESearchFilters.FILTER_RENTER_AGE],
      [ESearchFilters.PRICE_MIN]: searchFormFilters[ESearchFilters.PRICE_MIN],
      [ESearchFilters.PRICE_MAX]: searchFormFilters[ESearchFilters.PRICE_MAX],
      [ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES]:
        searchFormFilters[ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES],
      [ESearchFilters.CAMPSITE_SUPPORTED_LENGTH]:
        searchFormFilters[ESearchFilters.CAMPSITE_SUPPORTED_LENGTH],
      [ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS]:
        searchFormFilters[ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS],
    };

    if (!isRentalsVDeliverySearchModeEnabled) {
      more = {
        ...more,
        ...{
          [ESearchFilters.DELIVERY]: searchFormFilters[ESearchFilters.DELIVERY],
          [ESearchFilters.DELIVERY_ADDRESS]: searchFormFilters[ESearchFilters.DELIVERY_ADDRESS],
          [ESearchFilters.DELIVERY_CENTER]: searchFormFilters[ESearchFilters.DELIVERY_CENTER],
          [ESearchFilters.DELIVERY_LOCATION_ID]:
            searchFormFilters[ESearchFilters.DELIVERY_LOCATION_ID],
          [ESearchFilters.DELIVERY_QUERY]: searchFormFilters[ESearchFilters.DELIVERY_QUERY],
        },
      };
    }

    const make = {
      [ESearchFilters.FILTER_VEHICLE_MAKE]: searchFormFilters[ESearchFilters.FILTER_VEHICLE_MAKE],
      [ESearchFilters.FILTER_VEHICLE_MAKE_MODELS]:
        searchFormFilters[ESearchFilters.FILTER_VEHICLE_MAKE_MODELS],
      [ESearchFilters.YEAR_GREATER]: searchFormFilters[ESearchFilters.YEAR_GREATER],
      [ESearchFilters.YEAR_LESSER]: searchFormFilters[ESearchFilters.YEAR_LESSER],
    };

    type TMoreKey = keyof typeof more;
    type TMakeKey = keyof typeof make;

    const unsetMore = Object.keys(more).filter(k => typeof more[k as TMoreKey] === 'undefined');
    const setMore = Object.keys(more).reduce<Partial<Record<TMoreKey, string>>>((acc, cur) => {
      const key = cur as TMoreKey;
      return { ...acc, [key]: String(more[key]) };
    }, {});
    const setMake = Object.keys(make).reduce<Partial<Record<TMoreKey, string>>>((acc, cur) => {
      const key = cur as TMakeKey;
      return { ...acc, [key]: String(make[key]) };
    }, {});

    unsetMore.length && dispatch(unsetSearchFilter(unsetMore as ESearchFilters[]));
    (!isEmpty(setMore) || !isEmpty(setMake)) &&
      dispatch(
        applySearchFilter(
          {
            ...resetPageParams,
            ...(isEmpty(setMore) ? {} : setMore),
            ...(isEmpty(setMake) ? {} : setMake),
          },
          false,
          false,
          false,
          undefined,
          false,
        ),
      );
  }, [dispatch, resetPageParams, searchFormFilters, isRentalsVDeliverySearchModeEnabled]);

  const handleTypeFilterSubmit = useCallback(() => {
    const filterType = searchFormFilters[ESearchFilters.FILTER_TYPE];

    const filterResults = {
      [ESearchFilters.FILTER_TYPE]: Array.isArray(filterType) ? filterType.join(',') : undefined,
      [ESearchFilters.WEIGHT_LESSER]:
        searchFormFilters[ESearchFilters.WEIGHT_LESSER]?.toString() || undefined,
      [ESearchFilters.SUGGESTED_TOWING_CAPACITY]:
        searchFormFilters[ESearchFilters.SUGGESTED_TOWING_CAPACITY]?.toString() || undefined,
      [ESearchFilters.TOWING_ID]:
        searchFormFilters[ESearchFilters.TOWING_ID]?.toString() || undefined,
    };

    dispatch(
      !isEmpty(filterResults)
        ? applySearchFilter(
            {
              ...resetPageParams,
              ...filterResults,
            },
            false,
            false,
            false,
            undefined,
            false,
          )
        : unsetSearchFilter([
            ESearchFilters.FILTER_TYPE,
            ESearchFilters.WEIGHT_LESSER,
            ESearchFilters.SUGGESTED_TOWING_CAPACITY,
            ESearchFilters.TOWING_ID,
          ]),
    );
  }, [dispatch, resetPageParams, searchFormFilters]);

  const handleTypeFilterDismiss = useCallback(() => {
    const filterType = filtersFromQuery[ESearchFilters.FILTER_TYPE] as string;

    const filterResults = {
      [ESearchFilters.FILTER_TYPE]: filterType?.split(',') as TVehicleType[],
      [ESearchFilters.WEIGHT_LESSER]:
        Number(filtersFromQuery[ESearchFilters.WEIGHT_LESSER]) || undefined,
      [ESearchFilters.SUGGESTED_TOWING_CAPACITY]:
        Number(filtersFromQuery[ESearchFilters.SUGGESTED_TOWING_CAPACITY]) || undefined,
      [ESearchFilters.TOWING_ID]: Number(filtersFromQuery[ESearchFilters.TOWING_ID]) || undefined,
    };

    dispatch(setFormFilter({ filters: filterResults, filterMethod: null }));
  }, [dispatch, filtersFromQuery]);

  const handleRVProfileFilterSubmit = useCallback(() => {
    const filterSubTypes = searchFormFilters[ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES];
    const filterSlideouts = searchFormFilters[ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS];

    const filterResults = {
      [ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES]: Array.isArray(filterSubTypes)
        ? filterSubTypes.join(',')
        : undefined,
      [ESearchFilters.CAMPSITE_SUPPORTED_LENGTH]:
        searchFormFilters[ESearchFilters.CAMPSITE_SUPPORTED_LENGTH]?.toString() || undefined,
      [ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS]: Array.isArray(filterSlideouts)
        ? filterSlideouts.join(',')
        : undefined,
    };

    dispatch(
      !isEmpty(filterResults)
        ? applySearchFilter(
            {
              ...resetPageParams,
              ...filterResults,
            },
            false,
            false,
            false,
            undefined,
            false,
          )
        : unsetSearchFilter([
            ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES,
            ESearchFilters.CAMPSITE_SUPPORTED_LENGTH,
            ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS,
          ]),
    );
  }, [dispatch, resetPageParams, searchFormFilters]);

  const handleRVProfileFilterDismiss = useCallback(() => {
    const filterSubTypes = filtersFromQuery[ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES] as string;
    const filterSlideouts = filtersFromQuery[ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS] as string;

    const filterResults = {
      [ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES]: filterSubTypes?.split(',') as TVehicleType[],
      [ESearchFilters.CAMPSITE_SUPPORTED_LENGTH]:
        Number(filtersFromQuery[ESearchFilters.CAMPSITE_SUPPORTED_LENGTH]) || undefined,
      [ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS]: filterSlideouts?.split(','),
    };

    dispatch(setFormFilter({ filters: filterResults, filterMethod: null }));
  }, [dispatch, filtersFromQuery]);

  const handleDriverAgeFilterSubmit = useCallback(() => {
    const renterAgeValue = searchFormFilters[ESearchFilters.FILTER_RENTER_AGE];

    // Only apply filter if a value is selected
    const filterToApply = renterAgeValue
      ? {
          [ESearchFilters.FILTER_RENTER_AGE]: String(renterAgeValue),
        }
      : {};

    dispatch(
      applySearchFilter(
        { ...filterToApply, [ESearchFilters.PAGE_OFFSET]: String(0) },
        true,
        false,
        false,
        undefined,
        false,
      ),
    );
  }, [dispatch, searchFormFilters]);

  const handleDriverAgeFilterDismiss = useCallback(() => {
    const renterAge = filtersFromQuery[ESearchFilters.FILTER_RENTER_AGE];

    dispatch(
      setFormFilter({
        filters: {
          [ESearchFilters.FILTER_RENTER_AGE]: renterAge ? Number(renterAge) : 25,
        },
        filterMethod: null,
      }),
    );
  }, [dispatch, filtersFromQuery]);

  const handleMoreFilterDismiss = useCallback(() => {
    const filterType = filtersFromQuery[ESearchFilters.FILTER_TYPE] as string;
    const filterSubTypes = filtersFromQuery[ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES] as string;
    const filterSlideouts = filtersFromQuery[ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS] as string;

    let filterResults = {
      [ESearchFilters.FILTER_KEYWORDS]: filtersFromQuery[ESearchFilters.FILTER_KEYWORDS]
        ? String(filtersFromQuery[ESearchFilters.FILTER_KEYWORDS])
        : undefined,
      [ESearchFilters.YEAR_GREATER]:
        Number(filtersFromQuery[ESearchFilters.YEAR_GREATER]) || undefined,
      [ESearchFilters.YEAR_LESSER]:
        Number(filtersFromQuery[ESearchFilters.YEAR_LESSER]) || undefined,
      [ESearchFilters.FILTER_VEHICLE_MAKE]: filtersFromQuery[ESearchFilters.FILTER_VEHICLE_MAKE]
        ? String(filtersFromQuery[ESearchFilters.FILTER_VEHICLE_MAKE])
        : undefined,
      [ESearchFilters.FILTER_VEHICLE_MAKE_MODELS]: filtersFromQuery[
        ESearchFilters.FILTER_VEHICLE_MAKE_MODELS
      ]
        ? String(filtersFromQuery[ESearchFilters.FILTER_VEHICLE_MAKE_MODELS])
        : undefined,
      [ESearchFilters.SCORE]: Number(filtersFromQuery[ESearchFilters.SCORE]) || undefined,
      [ESearchFilters.UNLIMITED_GENERATOR]:
        filtersFromQuery[ESearchFilters.UNLIMITED_GENERATOR] == 'true',
      [ESearchFilters.UNLIMITED_MILES]: filtersFromQuery[ESearchFilters.UNLIMITED_MILES] == 'true',
      [ESearchFilters.CANCEL_POLICY]:
        (getParamAsString(filtersFromQuery[ESearchFilters.CANCEL_POLICY])?.split(
          ',',
        ) as TCancelPoliciesFilters[]) || undefined,
      [ESearchFilters.FILTER_FEATURE]:
        (getParamAsString(filtersFromQuery[ESearchFilters.FILTER_FEATURE])?.split(
          ',',
        ) as TAmenitySlug[]) || undefined,
      [ESearchFilters.WEIGHT_GREATER]:
        Number(filtersFromQuery[ESearchFilters.WEIGHT_GREATER]) || undefined,
      [ESearchFilters.WEIGHT_LESSER]:
        Number(filtersFromQuery[ESearchFilters.WEIGHT_LESSER]) || undefined,
      [ESearchFilters.LENGTH_BETWEEN]:
        getParamAsString(filtersFromQuery[ESearchFilters.LENGTH_BETWEEN])?.split(',') || undefined,
      [ESearchFilters.SEATBELTS]: Number(filtersFromQuery[ESearchFilters.SEATBELTS]) || undefined,
      [ESearchFilters.FILTER_VEHICLE_AMPS]:
        Number(filtersFromQuery[ESearchFilters.FILTER_VEHICLE_AMPS]) || undefined,
      [ESearchFilters.FILTER_OWNERTYPE]: filtersFromQuery[ESearchFilters.FILTER_OWNERTYPE]
        ? String(filtersFromQuery[ESearchFilters.FILTER_OWNERTYPE])
        : undefined,
      [ESearchFilters.FILTER_TAGS_SLUG]: filtersFromQuery[ESearchFilters.FILTER_TAGS_SLUG]
        ? String(filtersFromQuery[ESearchFilters.FILTER_TAGS_SLUG])
        : undefined,
      [ESearchFilters.FILTER_RENTER_AGE]:
        Number(filtersFromQuery[ESearchFilters.FILTER_RENTER_AGE]) || undefined,
      [ESearchFilters.PRICE_MIN]: Number(filtersFromQuery[ESearchFilters.PRICE_MIN]) || undefined,
      [ESearchFilters.PRICE_MAX]: Number(filtersFromQuery[ESearchFilters.PRICE_MAX]) || undefined,
      [ESearchFilters.FILTER_TYPE]: filterType
        ? (filterType.split(',') as TVehicleType[])
        : undefined,
      [ESearchFilters.INSTANT_BOOK]:
        filtersFromQuery[ESearchFilters.INSTANT_BOOK] === 'true' || undefined,
      [ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES]: filterSubTypes?.split(',') as TVehicleType[],
      [ESearchFilters.CAMPSITE_SUPPORTED_LENGTH]:
        Number(filtersFromQuery[ESearchFilters.CAMPSITE_SUPPORTED_LENGTH]) || undefined,
      [ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS]: filterSlideouts?.split(','),
    };
    if (!isRentalsVDeliverySearchModeEnabled) {
      filterResults = {
        ...filterResults,
        ...{
          [ESearchFilters.DELIVERY]: filtersFromQuery[ESearchFilters.DELIVERY] === 'true',
          [ESearchFilters.DELIVERY_ADDRESS]: filtersFromQuery[ESearchFilters.DELIVERY_ADDRESS]
            ? String(filtersFromQuery[ESearchFilters.DELIVERY_ADDRESS])
            : undefined,
          [ESearchFilters.DELIVERY_STATIONARY]: filtersFromQuery[ESearchFilters.DELIVERY_STATIONARY]
            ? String(filtersFromQuery[ESearchFilters.DELIVERY_STATIONARY])
            : undefined,
          [ESearchFilters.DELIVERY_DETAILS]: filtersFromQuery[ESearchFilters.DELIVERY_DETAILS]
            ? String(filtersFromQuery[ESearchFilters.DELIVERY_DETAILS])
            : undefined,
        },
      };
    }

    dispatch(setFormFilter({ filters: filterResults, filterMethod: null }));
  }, [dispatch, filtersFromQuery, isRentalsVDeliverySearchModeEnabled]);

  const filterFunctions = useMemo(
    () => ({
      [EFilterCategories.TYPE]: {
        onReset: () =>
          handleFiltersReset([
            ESearchFilters.FILTER_TYPE,
            ESearchFilters.WEIGHT_LESSER,
            ESearchFilters.SUGGESTED_TOWING_CAPACITY,
            ESearchFilters.TOWING_ID,
          ]),
        onSubmit: handleTypeFilterSubmit,
        onDismiss: handleTypeFilterDismiss,
      },
      [EFilterCategories.RV_PROFILE]: {
        onReset: () =>
          handleFiltersReset([
            ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES,
            ESearchFilters.CAMPSITE_SUPPORTED_LENGTH,
            ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS,
          ]),
        onSubmit: handleRVProfileFilterSubmit,
        onDismiss: handleRVProfileFilterDismiss,
      },
      [EFilterCategories.PRICE]: {
        onReset: () => handleFiltersReset([ESearchFilters.PRICE_MIN, ESearchFilters.PRICE_MAX]),
        onSubmit: handlePriceFilterSubmit,
        onDismiss: handlePriceFilterDismiss,
      },
      [EFilterCategories.DELIVERY]: {
        onReset: (valuesToRestore?: Partial<ISearchFormFilters>) =>
          handleFiltersReset(
            [
              ESearchFilters.DELIVERY,
              ESearchFilters.DELIVERY_ADDRESS,
              ESearchFilters.DELIVERY_CENTER,
              ESearchFilters.DELIVERY_LOCATION_ID,
              ESearchFilters.DELIVERY_QUERY,
              ESearchFilters.DELIVERY_STATIONARY,
              ESearchFilters.STATIONARY_ACKNOWLEDGED,
              ESearchFilters.DELIVERY_DETAILS,
              ESearchFilters.SEARCH_DELIVERY_CAMPGROUND_ID,
            ],
            [
              ESearchFilters.BOUNDS_NE,
              ESearchFilters.BOUNDS_SW,
              ESearchFilters.ADDRESS,
              ESearchFilters.MAPBOX_ID,
              ESearchFilters.FULL_MAPBOX_ADDRESS,
            ],
            valuesToRestore,
          ),
        onSubmit: handleDeliveryFilterSubmit,
        onDismiss: handleDeliveryFilterDismiss,
      },
      [EFilterCategories.DRIVER_AGE]: {
        onReset: () => handleFiltersReset([ESearchFilters.FILTER_RENTER_AGE]),
        onSubmit: handleDriverAgeFilterSubmit,
        onDismiss: handleDriverAgeFilterDismiss,
      },
      [EFilterCategories.MORE]: {
        onReset: () =>
          handleFiltersReset([
            ...[
              ESearchFilters.CANCEL_POLICY,
              ESearchFilters.FILTER_FEATURE,
              ESearchFilters.FILTER_KEYWORDS,
              ESearchFilters.LENGTH_BETWEEN,
              ESearchFilters.SCORE,
              ESearchFilters.UNLIMITED_MILES,
              ESearchFilters.UNLIMITED_GENERATOR,
              ESearchFilters.WEIGHT_GREATER,
              ESearchFilters.WEIGHT_LESSER,
              ESearchFilters.FILTER_VEHICLE_MAKE,
              ESearchFilters.FILTER_VEHICLE_MAKE_MODELS,
              ESearchFilters.YEAR_GREATER,
              ESearchFilters.YEAR_LESSER,
              ESearchFilters.SEATBELTS,
              ESearchFilters.FILTER_VEHICLE_AMPS,
              ESearchFilters.INSTANT_BOOK,
              ESearchFilters.FILTER_OWNERTYPE,
              ESearchFilters.FILTER_TAGS_SLUG,
              ESearchFilters.PRICE_MIN,
              ESearchFilters.PRICE_MAX,
              ESearchFilters.FILTER_TYPE,
              ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES,
              ESearchFilters.CAMPSITE_SUPPORTED_LENGTH,
              ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS,
            ],
            ...(!isRentalsVDeliverySearchModeEnabled
              ? [
                  ESearchFilters.DELIVERY,
                  ESearchFilters.DELIVERY_ADDRESS,
                  ESearchFilters.DELIVERY_CENTER,
                  ESearchFilters.DELIVERY_LOCATION_ID,
                  ESearchFilters.DELIVERY_QUERY,
                  ESearchFilters.DELIVERY_STATIONARY,
                  ESearchFilters.DELIVERY_DETAILS,
                ]
              : []),
          ]),
        onSubmit: handleMoreFilterSubmit,
        onDismiss: handleMoreFilterDismiss,
      },
    }),
    [
      handleDeliveryFilterDismiss,
      handleDeliveryFilterSubmit,
      handleDriverAgeFilterDismiss,
      handleDriverAgeFilterSubmit,
      handleFiltersReset,
      handleMoreFilterDismiss,
      handleMoreFilterSubmit,
      handlePriceFilterDismiss,
      handlePriceFilterSubmit,
      handleTypeFilterDismiss,
      handleTypeFilterSubmit,
      handleRVProfileFilterDismiss,
      handleRVProfileFilterSubmit,
      isRentalsVDeliverySearchModeEnabled,
    ],
  );

  return (
    <>
      {/* Renders modal with filter content and submit/reset buttons on desktop and mweb */}
      <FilterModalContainer
        isModalOpen={isModalOpen}
        handleDismiss={handleModalDismiss}
        filterFunctions={filterFunctions}
        deliveryFilterStep={deliveryFilterStep}
        setDeliveryFilterStep={setDeliveryFilterStep}
      />

      {/* Renders dropdown container for some of the filters on desktop */}
      {!isMobile && (
        <FilterDropdownContainer
          onDismiss={handleDropdownDismiss}
          triggerState={dropdownTriggerState}
          show={isDropdownOpen}
          triggerRef={triggerRef}
          filterFunctions={filterFunctions}
        />
      )}
    </>
  );
};

export default FiltersPillsContentContainer;
